import {
  bresenhamLine,
  brushCells,
  dilate,
  ellipseFill,
  ellipseOutline,
  floodFill,
  rectFill,
  rectOutline,
} from "#canvas/algorithms.ts";
import { Tool } from "#canvas/types.ts";
import type { Cell, Color, Layer, PixelOp, ToolMode } from "#canvas/types.ts";

/**
 * Pointer-gesture state. One discriminated union replaces the prior
 * `Stroke | null` ref + separate `panStartRef`. Each non-idle kind owns the
 * bookkeeping it actually needs — no cross-contamination.
 */
export type StrokeState =
  | { kind: "idle" }
  | { kind: "panning"; pointerX: number; pointerY: number; panBase: { x: number; y: number } }
  | { kind: "freehand"; tool: "DRAW" | "ERASER"; touched: Map<string, Cell> }
  | { kind: "shape"; tool: "LINE" | "RECTANGLE" | "ELLIPSE"; from: Cell }
  | { kind: "tap"; tool: "FILL" | "SELECT" };

/**
 * Translated React pointer events. The host hook computes `cell` via
 * `eventToCell` (viewport-aware) and forwards `clientX/Y` raw for pan deltas.
 * `cancel` carries no payload — it always discards.
 */
type StrokeEvent =
  | { type: "down"; cell: Cell | null; clientX: number; clientY: number; button: number; shiftKey: boolean }
  | { type: "move"; cell: Cell | null; clientX: number; clientY: number }
  | { type: "up"; cell: Cell | null }
  | { type: "cancel" };

/**
 * Side-effect surface the reducer drives. Carries both ops (callbacks) and
 * the live prop snapshot so the reducer can read current color/brushSize
 * etc. on each event — matches the prior closure-fresh semantic where
 * mid-stroke prop changes apply to subsequent cells.
 */
export interface StrokeOps {
  /** Mutate the live active layer with the given pixel writes; repaints its renderer. */
  applyLiveOps: (ops: PixelOp[]) => void;
  /** Snapshot live state into history and fire the parent `onCommit`. */
  commit: () => void;
  /** Drop any in-progress live edits and snap back to last committed state. No history push. */
  discardLive: () => void;
  /** Read the current live layer list (mid-stroke or post-commit). */
  getLive: () => Layer[];
  /** Paint the shape-preview offscreen with the given pixels; pass `[]` to clear. */
  paintPreview: (pixels: PixelOp[]) => void;
  /** Update the pan offset; called continuously during a pan gesture. */
  setPanDelta: (next: { x: number; y: number }) => void;
  /** Current tool mode — drives down-time routing into freehand/shape/tap. */
  mode: ToolMode;
  /** Active draw colour. Read fresh per event so mid-stroke colour changes apply. */
  color: Color;
  /** Active brush size, in cells. Read fresh per event. */
  brushSize: number;
  /** Outline-vs-fill toggle for shape tools (RECTANGLE, ELLIPSE). */
  shapeFill: boolean;
  /** Current pan offset; used as the `panBase` when entering a pan gesture. */
  panDelta: { x: number; y: number };
  /** Layer that draw/erase/fill writes target. */
  activeLayerId: string;
}

export const IDLE: StrokeState = { kind: "idle" };

function cellsToOps(cells: Cell[], color: Color): PixelOp[] {
  return cells.map((c) => ({ ...c, color }));
}

function shapeCells(
  tool: "LINE" | "RECTANGLE" | "ELLIPSE",
  from: Cell,
  to: Cell,
  brushSize: number,
  fill: boolean,
): Cell[] {
  if (tool === Tool.LINE) return dilate(bresenhamLine(from, to), brushSize);
  if (tool === Tool.RECTANGLE) return fill ? rectFill(from, to) : dilate(rectOutline(from, to), brushSize);
  return fill ? ellipseFill(from, to) : dilate(ellipseOutline(from, to), brushSize);
}

function isPanGesture(event: Extract<StrokeEvent, { type: "down" }>): boolean {
  return event.button === 1 || (event.button === 0 && event.shiftKey);
}

/**
 * Pure-ish reducer: returns the next state, runs side effects via `ops`.
 * Behaviour preserved from `PixelCanvas.tsx` pre-refactor with one
 * intentional fix — `cancel` now discards live edits instead of committing.
 *
 * @param state Current gesture state. Each non-idle kind owns its own bookkeeping.
 * @param event Pointer event (`down` / `move` / `up` / `cancel`) translated from the host's React handlers.
 * @param ops Side-effect surface plus a fresh prop snapshot. Read by transition handlers; never mutated by the reducer itself.
 * @returns The next gesture state. Most strokes terminate in `IDLE` on `up` or `cancel`.
 */
export function reduce(state: StrokeState, event: StrokeEvent, ops: StrokeOps): StrokeState {
  switch (state.kind) {
    case "idle":
      return reduceIdle(event, ops);
    case "panning":
      return reducePanning(state, event, ops);
    case "freehand":
      return reduceFreehand(state, event, ops);
    case "shape":
      return reduceShape(state, event, ops);
    case "tap":
      return reduceTap(state, event, ops);
  }
}

function reduceIdle(event: StrokeEvent, ops: StrokeOps): StrokeState {
  if (event.type !== "down") return IDLE;

  if (isPanGesture(event)) {
    return {
      kind: "panning",
      pointerX: event.clientX,
      pointerY: event.clientY,
      panBase: { ...ops.panDelta },
    };
  }
  if (event.button !== 0) return IDLE;
  if (!event.cell) return IDLE;
  if (ops.mode.kind === "picker") return IDLE; // handled on click in PixelCanvas

  const tool = ops.mode.brush;

  if (tool === Tool.DRAW || tool === Tool.ERASER) {
    const touched = new Map<string, Cell>();
    const useColor = tool === Tool.ERASER ? "" : ops.color;
    const cells = brushCells(event.cell, ops.brushSize);
    for (const c of cells) touched.set(`${c.row}|${c.col}`, c);
    ops.applyLiveOps(cellsToOps(cells, useColor));
    return { kind: "freehand", tool, touched };
  }

  if (tool === Tool.LINE || tool === Tool.RECTANGLE || tool === Tool.ELLIPSE) {
    return { kind: "shape", tool, from: event.cell };
  }

  if (tool === Tool.FILL) {
    const layer = ops.getLive().find((l) => l.id === ops.activeLayerId);
    if (layer) {
      const cells = floodFill(layer.data, event.cell, ops.color);
      if (cells.length > 0) ops.applyLiveOps(cellsToOps(cells, ops.color));
    }
    return { kind: "tap", tool: Tool.FILL };
  }

  return { kind: "tap", tool: Tool.SELECT };
}

function reducePanning(
  state: Extract<StrokeState, { kind: "panning" }>,
  event: StrokeEvent,
  ops: StrokeOps,
): StrokeState {
  if (event.type === "move") {
    ops.setPanDelta({
      x: state.panBase.x + (event.clientX - state.pointerX),
      y: state.panBase.y + (event.clientY - state.pointerY),
    });
    return state;
  }
  if (event.type === "up" || event.type === "cancel") return IDLE;
  return state;
}

function reduceFreehand(
  state: Extract<StrokeState, { kind: "freehand" }>,
  event: StrokeEvent,
  ops: StrokeOps,
): StrokeState {
  if (event.type === "move") {
    if (!event.cell) return state;
    const useColor = state.tool === Tool.ERASER ? "" : ops.color;
    const cells = brushCells(event.cell, ops.brushSize);
    const fresh: Cell[] = [];
    for (const c of cells) {
      const key = `${c.row}|${c.col}`;
      if (state.touched.has(key)) continue;
      state.touched.set(key, c);
      fresh.push(c);
    }
    if (fresh.length > 0) ops.applyLiveOps(cellsToOps(fresh, useColor));
    return state;
  }
  if (event.type === "up") {
    ops.commit();
    return IDLE;
  }
  if (event.type === "cancel") {
    ops.discardLive();
    return IDLE;
  }
  return state;
}

function reduceShape(
  state: Extract<StrokeState, { kind: "shape" }>,
  event: StrokeEvent,
  ops: StrokeOps,
): StrokeState {
  if (event.type === "move") {
    if (!event.cell) return state;
    const cells = shapeCells(state.tool, state.from, event.cell, ops.brushSize, ops.shapeFill);
    ops.paintPreview(cellsToOps(cells, ops.color));
    return state;
  }
  if (event.type === "up") {
    if (event.cell) {
      const cells = shapeCells(state.tool, state.from, event.cell, ops.brushSize, ops.shapeFill);
      ops.applyLiveOps(cellsToOps(cells, ops.color));
    }
    ops.paintPreview([]);
    ops.commit();
    return IDLE;
  }
  if (event.type === "cancel") {
    ops.paintPreview([]);
    ops.discardLive();
    return IDLE;
  }
  return state;
}

function reduceTap(
  state: Extract<StrokeState, { kind: "tap" }>,
  event: StrokeEvent,
  ops: StrokeOps,
): StrokeState {
  if (event.type === "up") {
    if (state.tool !== Tool.SELECT) ops.commit();
    return IDLE;
  }
  if (event.type === "cancel") {
    ops.discardLive();
    return IDLE;
  }
  return state;
}
